import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ShieldAlert, Check, X, Loader2 } from 'lucide-react';
import { useTranslation } from '@/i18n';
import { apiClient, HITLPendingItem } from '@/utils/apiClient';

interface HitlApprovalsProps {
  /** Bump this to trigger an immediate refresh (e.g. after a chat turn). */
  refreshKey?: number;
  /** Called after an approved write executes, with the tool result text. */
  onApproved?: (toolName: string, result: string) => void;
  className?: string;
}

const POLL_INTERVAL_MS = 8000;

/**
 * STORY-165 — surfaces write/delete/admin tool calls that are awaiting human
 * approval and lets an admin Approve (re-invokes the tool via the backend) or
 * Deny them. Polls /api/hitl/pending; hidden entirely when nothing is pending.
 */
export const HitlApprovals: React.FC<HitlApprovalsProps> = ({
  refreshKey,
  onApproved,
  className,
}) => {
  const { t } = useTranslation();
  const h = ((t as any).hitl) || {};
  const [pending, setPending] = useState<HITLPendingItem[]>([]);
  const [busy, setBusy] = useState<Record<string, 'approve' | 'deny'>>({});
  const [error, setError] = useState<string | null>(null);
  // STORY-275: what the last approve/deny DID. Before, an approved write's
  // result was handed to an onApproved callback nobody passed, and the banner
  // vanished with the pending item — the approver never saw whether the rule
  // was created and verified, or that a denial wrote nothing.
  const [outcome, setOutcome] = useState<
    { kind: 'approved' | 'denied'; toolName: string; text: string } | null
  >(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchPending = useCallback(async () => {
    try {
      const res = await apiClient.getHitlPending();
      setPending(res.pending || []);
      setError(null);
    } catch {
      // Backend/MCP unreachable — just clear; the banner disappears.
      setPending([]);
    }
  }, []);

  useEffect(() => {
    fetchPending();
    timerRef.current = setInterval(fetchPending, POLL_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [fetchPending]);

  useEffect(() => {
    if (refreshKey !== undefined) fetchPending();
  }, [refreshKey, fetchPending]);

  const handleApprove = async (item: HITLPendingItem) => {
    setBusy((b) => ({ ...b, [item.confirmation_id]: 'approve' }));
    setError(null);
    try {
      const res = await apiClient.approveHitl(item.confirmation_id);
      setOutcome({ kind: 'approved', toolName: item.tool_name, text: res.result || '' });
      onApproved?.(item.tool_name, res.result || '');
      await fetchPending();
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setBusy((b) => {
        const next = { ...b };
        delete next[item.confirmation_id];
        return next;
      });
    }
  };

  const handleDeny = async (item: HITLPendingItem) => {
    setBusy((b) => ({ ...b, [item.confirmation_id]: 'deny' }));
    setError(null);
    try {
      await apiClient.denyHitl(item.confirmation_id);
      setOutcome({ kind: 'denied', toolName: item.tool_name, text: '' });
      await fetchPending();
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setBusy((b) => {
        const next = { ...b };
        delete next[item.confirmation_id];
        return next;
      });
    }
  };

  if (pending.length === 0 && !outcome && !error) return null;

  return (
    <div
      className={`mx-4 mb-2 rounded-md border border-amber-300 bg-amber-50 dark:border-amber-700/60 dark:bg-amber-900/20 ${className || ''}`}
    >
      {pending.length > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 border-b border-amber-200 dark:border-amber-700/50">
          <ShieldAlert className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <span className="text-sm font-medium text-amber-800 dark:text-amber-200">
            {h.title || 'Writes awaiting your approval'} ({pending.length})
          </span>
        </div>
      )}
      <ul className="divide-y divide-amber-200 dark:divide-amber-700/40">
        {pending.map((item) => {
          const b = busy[item.confirmation_id];
          return (
            <li key={item.confirmation_id} className="px-3 py-2">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-mono font-medium text-gray-900 dark:text-gray-100 truncate">
                    {item.tool_name}
                  </div>
                  <pre className="mt-1 whitespace-pre-wrap break-words text-xs text-gray-600 dark:text-gray-300">
                    {item.preview}
                  </pre>
                  <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                    {(h.expiresIn || 'expires in')} {item.expires_in_seconds}s
                    {!item.can_approve && (
                      <span className="ml-2 text-amber-700 dark:text-amber-300">
                        · {h.argsUnavailable || 'args not held here — re-run the query'}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-shrink-0 gap-1">
                  <button
                    onClick={() => handleApprove(item)}
                    disabled={!!b || !item.can_approve}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors"
                    title={h.approve || 'Approve'}
                  >
                    {b === 'approve' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                    {h.approve || 'Approve'}
                  </button>
                  <button
                    onClick={() => handleDeny(item)}
                    disabled={!!b}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-200 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 rounded transition-colors"
                    title={h.deny || 'Deny'}
                  >
                    {b === 'deny' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                    {h.deny || 'Deny'}
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      {outcome && (
        <div
          role="status"
          data-hitl-outcome={outcome.kind}
          className="flex items-start justify-between gap-3 px-3 py-2 border-t border-amber-200 dark:border-amber-700/50"
        >
          <div className="min-w-0 text-xs text-gray-700 dark:text-gray-200">
            <div className="font-medium">
              {outcome.kind === 'approved'
                ? (h.approvedResult || 'Approved and executed:')
                : (h.deniedResult || 'Denied — nothing was written:')}
              {' '}
              <span className="font-mono">{outcome.toolName}</span>
            </div>
            {outcome.text && (
              <pre className="mt-1 whitespace-pre-wrap break-words">{outcome.text}</pre>
            )}
          </div>
          <button
            type="button"
            onClick={() => setOutcome(null)}
            aria-label={h.dismiss || 'Dismiss'}
            title={h.dismiss || 'Dismiss'}
            className="flex-shrink-0 p-1 text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 rounded"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      {error && (
        <div className="px-3 py-2 text-xs text-red-600 dark:text-red-400 border-t border-amber-200 dark:border-amber-700/50">
          {error}
        </div>
      )}
    </div>
  );
};
